import { Component, input, OnInit, signal, viewChild } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { WordToKlankConverter } from '../../utils/klank.utils';
import { RidReadingCountdown } from '../rid-reading-countdown/rid-reading-countdown';

@Component({
  selector: 'rid-sentence',
  imports: [RidReadingCountdown],
  templateUrl: './rid-sentence.html',
  host: {
    class: 'block relative',
  },
})
export class RidSentence implements OnInit {
  sentence = input.required<string>();

  countdownComp = viewChild.required<RidReadingCountdown>('countdown');
  text = signal<{ text: string; opacity: number; scale: number; duration: number }[]>([]);
  state = signal<'BLANK' | 'COUNTDOWN' | 'PASS' | 'ASSESS' | 'REVEAL'>('BLANK');

  ngOnInit(): void {
    const words = this.sentence().split(' ');
    this.text.set(
      words.flatMap((word) => {
        const sounds = WordToKlankConverter.breakupWord(word);
        return sounds.map((sound, index) => {
          const isLastSound = index === sounds.length - 1;
          return {
            text: sound + (isLastSound ? ' ' : ''),
            opacity: 0,
            duration: 0,
            scale: 1,
          };
        });
      }),
    );
  }

  show() {
    this.state.set('REVEAL');
    this.text.update((current) => {
      return current.map(({ text }) => ({ text, opacity: 1, scale: 1, duration: 200 }));
    });
  }

  hide() {
    this.state.set('BLANK');
  }

  async countdown(pace: number) {
    this.state.set('COUNTDOWN');
    await delay(500);

    await this.countdownComp().countdown(5 * pace);
  }

  async pass(pace: number, initial: { opacity: number; scale: number }, pass: { opacity: number; scale: number }) {
    this.changeAppearance(0, { opacity: 0, scale: initial.scale });

    await this.countdown(pace);

    this.state.set('PASS');
    await this.changeAppearance(250, initial);
    await delay(250);

    for (let i = 0; i < this.text().length; i++) {
      this.text.update((current) => {
        const newText = [...current];
        newText[i] = { text: current[i].text, opacity: pass.opacity, scale: pass.scale, duration: pace };
        return newText;
      });
      await delay(pace);
    }

    await delay(2 * pace);

    this.changeAppearance(250, { opacity: 0 });
    this.assess();
  }

  private async changeAppearance(duration: number, properties: Partial<{ opacity: number; scale: number }>) {
    this.text.update((current) => {
      return current.map((sentence) => ({ ...sentence, ...properties, duration: duration }));
    });

    await delay(duration ?? 0);
  }

  public assess() {
    this.state.set('ASSESS');
  }
}
